/**
 * Process Steps Section — modal frontend behavior.
 */
import {
	lockBackgroundScroll,
	unlockBackgroundScroll,
} from '../shared/background-scroll-lock';
import {
	MODAL_OPEN_CLASS,
	animateModalClose,
	animateModalOpen,
	resetModalScroll,
} from '../shared/modal-animation';
import { MODAL_BODY_CLASS } from './modal-constants';
import { isUndersizedThemeIcon, getDefaultStepIcon } from './constants';
import { decodeHtmlEntities, findStepModalData } from './modal-utils';
import { renderModalBodyHtml } from './modal-render';

( function () {
	'use strict';

	const OPEN_CLASS = MODAL_OPEN_CLASS;
	let activeController = null;

	/**
	 * @param {HTMLElement} root Root element to search within.
	 * @return {HTMLElement[]} Focusable elements inside the root.
	 */
	function getFocusableElements( root ) {
		const selectors = [
			'a[href]',
			'button:not([disabled])',
			'textarea:not([disabled])',
			'input:not([disabled])',
			'select:not([disabled])',
			'[tabindex]:not([tabindex="-1"])',
		];

		return Array.from(
			root.querySelectorAll( selectors.join( ',' ) )
		).filter(
			( element ) =>
				! element.hasAttribute( 'disabled' ) &&
				element.getAttribute( 'aria-hidden' ) !== 'true'
		);
	}

	/**
	 * @param {HTMLElement} section Section root.
	 * @return {Object|null} Parsed modal section data or null when invalid.
	 */
	function readSectionData( section ) {
		const script = section.querySelector(
			'.alteia-process-steps-section__modal-data'
		);

		if ( ! script?.textContent ) {
			return null;
		}

		try {
			return JSON.parse( script.textContent );
		} catch {
			return null;
		}
	}

	/**
	 * @param {KeyboardEvent} event Keyboard event.
	 */
	function onGlobalKeydown( event ) {
		if ( ! activeController ) {
			return;
		}

		activeController.handleKeydown( event );
	}

	document.addEventListener( 'keydown', onGlobalKeydown );

	/**
	 * @param {HTMLElement} section Section element.
	 */
	function initProcessStepsModalSection( section ) {
		const sectionData = readSectionData( section );
		const modal = section.querySelector( '[data-pss-modal-root="true"]' );

		if ( ! sectionData || ! modal ) {
			return;
		}

		section
			.querySelectorAll( '.alteia-process-steps-section__step' )
			.forEach( ( stepEl, index ) => {
				const img = stepEl.querySelector(
					'.alteia-process-steps-section__icon'
				);
				const src = img?.getAttribute( 'src' ) || '';

				if ( img && isUndersizedThemeIcon( src ) ) {
					img.src = getDefaultStepIcon( index );
				}
			} );

		const dialog = modal.querySelector( '.alteia-pss-modal__dialog' );
		const inner = modal.querySelector( '.alteia-pss-modal__inner' );
		const title = modal.querySelector( '.alteia-pss-modal__title' );
		const body = modal.querySelector( '.alteia-pss-modal__body' );
		const closeButton = modal.querySelector( '.alteia-pss-modal__close' );
		const overlay = modal.querySelector( '.alteia-pss-modal__overlay' );

		let lastTrigger = null;

		/**
		 * @param {boolean} isOpen Whether the modal is open.
		 */
		function setModalFocusability( isOpen ) {
			if ( overlay ) {
				overlay.tabIndex = isOpen ? 0 : -1;
			}

			if ( closeButton ) {
				closeButton.tabIndex = isOpen ? 0 : -1;
			}
		}

		/**
		 * @param {Object} stepData Step modal data.
		 */
		function populateModal( stepData ) {
			if ( title ) {
				title.textContent = decodeHtmlEntities(
					stepData.modalTitle || stepData.title || ''
				);
			}

			if ( body ) {
				body.innerHTML = renderModalBodyHtml( stepData );
			}
		}

		/**
		 * Close the modal after the leave transition completes.
		 */
		function closeModal() {
			if ( ! modal.classList.contains( OPEN_CLASS ) ) {
				return;
			}

			modal.setAttribute( 'aria-hidden', 'true' );
			setModalFocusability( false );
			unlockBackgroundScroll( { bodyClass: MODAL_BODY_CLASS } );

			if ( activeController === controller ) {
				activeController = null;
			}

			animateModalClose( modal, {
				openClass: OPEN_CLASS,
				onClosed() {
					modal.hidden = true;
					if ( body ) {
						body.innerHTML = '';
					}
					if (
						lastTrigger &&
						typeof lastTrigger.focus === 'function' &&
						document.contains( lastTrigger )
					) {
						lastTrigger.focus();
					}
					lastTrigger = null;
				},
			} );
		}

		/**
		 * @param {string}            stepId  Step id.
		 * @param {HTMLButtonElement} trigger Trigger button.
		 */
		function openModal( stepId, trigger ) {
			const stepData = findStepModalData( sectionData, stepId );

			if ( ! stepData ) {
				return;
			}

			if (
				activeController &&
				activeController !== controller &&
				typeof activeController.close === 'function'
			) {
				activeController.close();
			}

			lastTrigger = trigger;
			populateModal( stepData );

			modal.hidden = false;
			modal.setAttribute( 'aria-hidden', 'false' );
			setModalFocusability( true );
			lockBackgroundScroll( {
				bodyClass: MODAL_BODY_CLASS,
				compensateScrollbar: true,
			} );
			resetModalScroll( modal );
			resetModalScroll( inner );
			resetModalScroll( body );
			resetModalScroll( dialog );

			activeController = controller;

			animateModalOpen( modal, {
				openClass: OPEN_CLASS,
				onOpened() {
					resetModalScroll( modal );
					resetModalScroll( inner );
					resetModalScroll( body );
					if ( closeButton ) {
						closeButton.focus();
					}
				},
			} );
		}

		/**
		 * @param {KeyboardEvent} event Keyboard event.
		 */
		function handleKeydown( event ) {
			if ( ! modal.classList.contains( OPEN_CLASS ) ) {
				return;
			}

			if ( event.key === 'Escape' ) {
				event.preventDefault();
				closeModal();
				return;
			}

			if ( event.key === 'Tab' && dialog ) {
				const focusables = getFocusableElements( dialog );
				if ( focusables.length === 0 ) {
					return;
				}

				const first = focusables[ 0 ];
				const last = focusables[ focusables.length - 1 ];
				const active = modal.ownerDocument.activeElement;

				if ( event.shiftKey && active === first ) {
					event.preventDefault();
					last.focus();
				} else if ( ! event.shiftKey && active === last ) {
					event.preventDefault();
					first.focus();
				}
			}
		}

		const controller = { close: closeModal, handleKeydown, modal };

		section.addEventListener( 'click', ( event ) => {
			const trigger = event.target.closest(
				'[data-modal-trigger="true"]'
			);

			if ( ! trigger || ! section.contains( trigger ) ) {
				return;
			}

			if (
				trigger.dataset.blockInstanceId &&
				sectionData.blockInstanceId &&
				trigger.dataset.blockInstanceId !== sectionData.blockInstanceId
			) {
				return;
			}

			event.preventDefault();
			openModal( trigger.dataset.stepId || '', trigger );
		} );

		modal.addEventListener( 'click', ( event ) => {
			if ( event.target.closest( '[data-pss-modal-close="true"]' ) ) {
				event.preventDefault();
				closeModal();
			}
		} );

		if ( dialog ) {
			dialog.addEventListener( 'click', ( event ) => {
				if ( event.target.closest( '[data-pss-modal-close="true"]' ) ) {
					event.preventDefault();
					closeModal();
					return;
				}

				event.stopPropagation();
			} );
		}

		if ( closeButton ) {
			closeButton.addEventListener( 'click', ( event ) => {
				event.preventDefault();
				closeModal();
			} );
		}

		setModalFocusability( false );
	}

	document.addEventListener( 'DOMContentLoaded', () => {
		document
			.querySelectorAll(
				'.alteia-process-steps-section--variant-with-modal'
			)
			.forEach( initProcessStepsModalSection );
	} );
} )();
